/**
 * RTK Query slice for LiftCloud's development-only Simulation API.
 *
 * The cloud mounts `/simulation/*` only on development hosts and answers 404
 * there unless the operator turned it on; every route is MASTER ADMIN only.
 * Responses are LN2E-enveloped FlatBuffers that the schema registry
 * (`flatbuffers/api/registrations.ts`) unpacks into `{ status, data }`, where
 * `data` is the generated Object API value; the endpoints below unwrap `data`
 * so components hold the `*T` types directly. A scenario is sent as its
 * `SimulationScenarioT` through the `SaveSimulationScenario` serializer.
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import type { SimulationAssetT } from "../../flatbuffers/generated/api/simulation-asset";
import type { SimulationAssetListT } from "../../flatbuffers/generated/api/simulation-asset-list";
import type { SimulationRunT } from "../../flatbuffers/generated/api/simulation-run";
import type { SimulationRunListT } from "../../flatbuffers/generated/api/simulation-run-list";
import type { SimulationSandboxT } from "../../flatbuffers/generated/api/simulation-sandbox";
import type { SimulationSandboxListT } from "../../flatbuffers/generated/api/simulation-sandbox-list";
import type { SimulationScenarioT } from "../../flatbuffers/generated/api/simulation-scenario";
import type { SimulationScenarioListT } from "../../flatbuffers/generated/api/simulation-scenario-list";
import type { SimulationTargetT } from "../../flatbuffers/generated/api/simulation-target";
import type { SimulationTargetListT } from "../../flatbuffers/generated/api/simulation-target-list";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

/** What the schema registry's single-entity handler resolves a response to. */
interface Unpacked<T> {
  status: string;
  data: T;
}

/** The kinds of file the Simulation API accepts, as its `kind` form field spells them. */
export type SimulationAssetUpload = "capture" | "opcua_domain";

/** Arguments every call carries: the caller's JWT, sent as `authorization`. */
interface Authorized {
  token: string;
}

interface OrgScoped extends Authorized {
  orgId: string;
}

interface ById extends Authorized {
  id: string;
}

/** Upload of one capture or OPC UA domain file to an organization's library. */
export interface UploadSimulationAssetArgs extends OrgScoped {
  kind: SimulationAssetUpload;
  file: File;
  /** Display name; the file name when omitted. */
  name?: string;
}

/** The scenario to save, held as the generated Object API value. */
export interface SaveSimulationScenarioArgs extends Authorized {
  scenario: SimulationScenarioT;
}

/** A new scenario for an organization; the server assigns its id. */
export interface CreateSimulationScenarioArgs extends SaveSimulationScenarioArgs {
  orgId: string;
}

/** A saved scenario's replacement, addressed by its id. */
export interface UpdateSimulationScenarioArgs extends SaveSimulationScenarioArgs {
  id: string;
}

export const simulationAPI = createApi({
  reducerPath: "simulationAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["SimulationAssets", "SimulationScenarios", "SimulationRuns"],
  endpoints: (builder) => ({
    listSimulationAssets: builder.query({
      query: ({ orgId, token }: OrgScoped) => ({
        url: `/simulation/organizations/${orgId}/assets`,
        headers: { authorization: token },
      }),
      transformResponse: (response: Unpacked<SimulationAssetListT>): SimulationAssetT[] =>
        response.data.assets,
      providesTags: ["SimulationAssets"],
    }),
    uploadSimulationAsset: builder.mutation({
      query: ({ orgId, token, kind, file, name }: UploadSimulationAssetArgs) => {
        const formData = new FormData();
        formData.append("kind", kind);
        formData.append("file", file);
        if (name) {
          formData.append("name", name);
        }
        return {
          url: `/simulation/organizations/${orgId}/assets`,
          method: "POST",
          body: formData,
          headers: { authorization: token },
        };
      },
      // An upload is not idempotent: a retried one would store the file twice.
      extraOptions: { maxRetries: 0 },
      transformResponse: (response: Unpacked<SimulationAssetT>): SimulationAssetT => response.data,
      invalidatesTags: ["SimulationAssets"],
    }),
    deleteSimulationAsset: builder.mutation({
      query: ({ id, token }: ById) => ({
        url: `/simulation/assets/${id}`,
        method: "DELETE",
        headers: { authorization: token },
      }),
      // The cloud answers 204 with no body.
      transformResponse: (): null => null,
      invalidatesTags: ["SimulationAssets"],
    }),
    listSimulationScenarios: builder.query({
      query: ({ orgId, token }: OrgScoped) => ({
        url: `/simulation/organizations/${orgId}/scenarios`,
        headers: { authorization: token },
      }),
      transformResponse: (response: Unpacked<SimulationScenarioListT>): SimulationScenarioT[] =>
        response.data.scenarios,
      providesTags: ["SimulationScenarios"],
    }),
    createSimulationScenario: builder.mutation({
      query: ({ orgId, token, scenario }: CreateSimulationScenarioArgs) => ({
        url: `/simulation/organizations/${orgId}/scenarios`,
        method: "POST",
        body: { scenario },
        headers: { authorization: token },
      }),
      extraOptions: { fbSerializer: "SaveSimulationScenario", maxRetries: 0 },
      transformResponse: (response: Unpacked<SimulationScenarioT>): SimulationScenarioT =>
        response.data,
      invalidatesTags: ["SimulationScenarios"],
    }),
    updateSimulationScenario: builder.mutation({
      query: ({ id, token, scenario }: UpdateSimulationScenarioArgs) => ({
        url: `/simulation/scenarios/${id}`,
        method: "PUT",
        body: { scenario },
        headers: { authorization: token },
      }),
      extraOptions: { fbSerializer: "SaveSimulationScenario" },
      transformResponse: (response: Unpacked<SimulationScenarioT>): SimulationScenarioT =>
        response.data,
      invalidatesTags: ["SimulationScenarios"],
    }),
    deleteSimulationScenario: builder.mutation({
      query: ({ id, token }: ById) => ({
        url: `/simulation/scenarios/${id}`,
        method: "DELETE",
        headers: { authorization: token },
      }),
      // The cloud answers 204 with no body.
      transformResponse: (): null => null,
      invalidatesTags: ["SimulationScenarios"],
    }),
    listSimulationTargets: builder.query({
      query: ({ orgId, token }: OrgScoped) => ({
        url: `/simulation/organizations/${orgId}/targets`,
        headers: { authorization: token },
      }),
      transformResponse: (response: Unpacked<SimulationTargetListT>): SimulationTargetT[] =>
        response.data.targets,
    }),
    listSimulationSandboxes: builder.query({
      query: ({ token }: Authorized) => ({
        url: "/simulation/sandboxes",
        headers: { authorization: token },
      }),
      transformResponse: (response: Unpacked<SimulationSandboxListT>): SimulationSandboxT[] =>
        response.data.sandboxes,
    }),
    startSimulationRun: builder.mutation({
      query: ({ id, token }: ById) => ({
        url: `/simulation/scenarios/${id}/runs`,
        method: "POST",
        headers: { authorization: token },
      }),
      // Starting twice would replace the first run's tunnel session.
      extraOptions: { maxRetries: 0 },
      transformResponse: (response: Unpacked<SimulationRunT>): SimulationRunT => response.data,
      invalidatesTags: ["SimulationRuns"],
    }),
    listSimulationRuns: builder.query({
      query: ({ orgId, token }: OrgScoped) => ({
        url: `/simulation/organizations/${orgId}/runs`,
        headers: { authorization: token },
      }),
      transformResponse: (response: Unpacked<SimulationRunListT>): SimulationRunT[] =>
        response.data.runs,
      providesTags: ["SimulationRuns"],
    }),
    getSimulationRun: builder.query({
      query: ({ id, token }: ById) => ({
        url: `/simulation/runs/${id}`,
        headers: { authorization: token },
      }),
      transformResponse: (response: Unpacked<SimulationRunT>): SimulationRunT => response.data,
    }),
    stopSimulationRun: builder.mutation({
      query: ({ id, token }: ById) => ({
        url: `/simulation/runs/${id}/stop`,
        method: "POST",
        headers: { authorization: token },
      }),
      transformResponse: (response: Unpacked<SimulationRunT>): SimulationRunT => response.data,
      invalidatesTags: ["SimulationRuns"],
    }),
  }),
});

export const {
  useListSimulationAssetsQuery,
  useLazyListSimulationAssetsQuery,
  useUploadSimulationAssetMutation,
  useDeleteSimulationAssetMutation,
  useListSimulationScenariosQuery,
  useLazyListSimulationScenariosQuery,
  useCreateSimulationScenarioMutation,
  useUpdateSimulationScenarioMutation,
  useDeleteSimulationScenarioMutation,
  useListSimulationTargetsQuery,
  useLazyListSimulationTargetsQuery,
  useListSimulationSandboxesQuery,
  useLazyListSimulationSandboxesQuery,
  useStartSimulationRunMutation,
  useListSimulationRunsQuery,
  useLazyListSimulationRunsQuery,
  useGetSimulationRunQuery,
  useLazyGetSimulationRunQuery,
  useStopSimulationRunMutation,
} = simulationAPI;
